import type { StatusTone } from '@beonit/adaptive-raindrop';
import type {
  AnalysisConfigSummary,
  CoherenceControlMetrics,
  ControlExecution,
  ControlGroup,
  ControlKind,
  ControlLaunchParameters,
  ControlMissing,
  ControlPendingVideo,
  GestureControlMetrics,
  GroupBatch,
  GroupExecutionList,
  GroupLaunchParameters,
  MethodDelta,
} from '../model.js';
import { DEFAULT_ENGINE_CATALOGUE, engineLabel } from './engine-rules.js';
import { executionParameters, reanalysisFlag } from './group-history.js';
import { launchWillQueue } from './group-launch.js';
import { configNameResolver } from './group-parameters.js';
import type { LaunchPreset } from './launch-execution.js';
import { figureField } from './metric-explanations.js';
import { metricsReading, readingText } from './metrics-reading.js';
import { type MetricDirection, type MetricStatus, SERIES_METRICS } from './metrics-series.js';
import { formatPct } from './product-metric.js';
import { rawValue } from './version-series.js';

/**
 * The control groups (product owner, 2026-09-28; static since 2026-10-06): four analysis groups
 * the system creates and nobody deletes, one per kind, so technology, method and product are
 * measured apart.
 *
 * - **Gestos** measures the gestures engine alone: its detections against the gestures a person
 *   labelled on the same videos.
 * - **Alertas gestuales** (`gesture_alerts`, 2026-10-28; alerts only since 2026-10-29) measures
 *   the gestures engine's habituality rules: the alerts a person labelled on the same videos against
 *   the episodes the engine raised as alerts, with Gestos's three figures over those inputs.
 * - **Semántica** measures the methodology alone: the veracity engine (`coherence` upstream) fed the
 *   alerts a person labelled (scope `gesture_alerts`, 2026-10-29) instead of the engine's, against
 *   the Verdad/Mentira labels, so a miss is the rules' and not the detector's.
 * - **Veracidad** (`coherence` upstream) measures gestures and rules together, as a real analysis
 *   runs them.
 *
 * On screen the product speaks of «veracidad» again (product owner, 2026-10-20) while every
 * identifier upstream keeps its name: the kind and the engine are still `coherence`.
 *
 * Each kind has its business rule upstream — what a video needs before it counts — and upstream
 * says whether the group meets it (`ready`: its figures speak for the whole group) and which videos
 * do not yet (`pending`). «Ejecutar» does not wait for it: a run leaves the pending videos out, and
 * the launch dialog says so. What else it checks arrives as codes (`warnings`) and is worded here,
 * with the counts that make it actionable. The figures always say which execution they come from.
 *
 * ## The gestures control's targets
 *
 * Upstream judges each video correct at 80 % detection and 20 % false positives at most
 * (`VIDEO_CORRECT_MIN_DETECTION`, `VIDEO_CORRECT_MAX_FALSE_POSITIVE`); the group's figures are held
 * to the same two, and «Vídeos correctos» to the product's 70 %. Like `SERIES_METRICS`, they should
 * come from Virtual Profiler the day it serves them.
 */

export const CONTROL_KIND_TITLES: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures: 'Gestos',
  gesture_alerts: 'Alertas gestuales',
  semantics: 'Semántica',
  coherence: 'Veracidad',
});

/**
 * The controls the screens offer, in the product's order: the gestures engine alone, its alerts,
 * the methodology alone, then both together. Semántica, retired from the screens on 2026-10-14, is back
 * since 2026-10-20 (product owner); its history was never deleted.
 */
export const SHOWN_CONTROL_KINDS: readonly ControlKind[] = Object.freeze([
  'gestures',
  'gesture_alerts',
  'semantics',
  'coherence',
]);

export function isShownControl(kind: ControlKind): boolean {
  return SHOWN_CONTROL_KINDS.includes(kind);
}

/** An engine a control group measures, as upstream names it: the veracity engine is `coherence`. */
export type ControlEngine = 'gestures' | 'coherence';

/** One engine's box on «Control»: its band, over the scoreboards of the controls that measure it. */
export interface ControlPanel {
  engine: ControlEngine;
  /** Its controls' scoreboards, top to bottom. */
  kinds: readonly ControlKind[];
}

/**
 * «Control», one box per engine (product owner, 2026-10-20): Gestos holds its control group and,
 * under it, Alertas gestuales — the same gestures engine, measured on its alerts —, and Veracidad
 * holds the Veracidad control and, under it, Semántica — the same veracity engine (`coherence`)
 * fed the hand-labelled alerts, a control of that engine and not an engine of its own. Two
 * boxes, not four. The lists keep the kinds in the product's order (`SHOWN_CONTROL_KINDS`).
 */
export const CONTROL_PANELS: readonly ControlPanel[] = Object.freeze([
  // Veracidad on top, its own control first (product owner, 2026-10-28).
  {
    engine: 'coherence',
    kinds: Object.freeze<ControlKind[]>(['coherence', 'semantics']),
  },
  {
    engine: 'gestures',
    kinds: Object.freeze<ControlKind[]>(['gestures', 'gesture_alerts']),
  },
]);

/**
 * Which engines each control measures, for the board on «Control» (product owner, 2026-10-28): a
 * band from every engine a control reads to the control itself. Gestos and Alertas gestuales read
 * the gestures engine alone; Semántica the veracity engine alone (fed the hand-labelled alerts);
 * Veracidad both. A new control is one more entry here and one more kind in `SHOWN_CONTROL_KINDS`;
 * the board draws whatever is listed.
 */
export const CONTROL_ENGINES: Readonly<Record<ControlKind, readonly ControlEngine[]>> =
  Object.freeze({
    gestures: Object.freeze<ControlEngine[]>(['gestures']),
    gesture_alerts: Object.freeze<ControlEngine[]>(['gestures']),
    semantics: Object.freeze<ControlEngine[]>(['coherence']),
    coherence: Object.freeze<ControlEngine[]>(['gestures', 'coherence']),
  });

/**
 * The line under each scoreboard's title on «Control» and on the control's page: what it compares,
 * as short as it can be said (product owner, 2026-10-20).
 */
export const CONTROL_KIND_SUMMARIES: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures: 'Motor Gestos vs etiquetas de detección gestual',
  gesture_alerts: 'Motor Gestos vs etiquetas de alerta gestual',
  semantics: '(Motor Veracidad + etiquetas de alertas gestuales) vs etiquetas de veracidad',
  coherence: '(Motor Veracidad + Motor Gestos) vs etiquetas de veracidad',
});

/**
 * «Producto»: the Veracidad control measures the product itself, the two engines chained as the
 * client sees them (product owner, 2026-10-29). The other controls measure one piece of it.
 */
export const PRODUCT_CONTROL_KIND: ControlKind = 'coherence';

export function isProductControl(kind: ControlKind): boolean {
  return kind === PRODUCT_CONTROL_KIND;
}

export function controlScoreboardNote(kind: ControlKind): string {
  return CONTROL_KIND_SUMMARIES[kind];
}

/**
 * What the control groups are, for the «i» beside their title — and, on «Control», beside
 * «Motores», which holds them (product owner, 2026-10-14).
 */
export const CONTROL_GROUPS_INFO =
  'Cuatro grupos fijos del sistema: Gestos, Alertas gestuales, Semántica y Veracidad. Sus vídeos se cambian en cada grupo.';

/**
 * What each control does and what it measures, the product owner's words (2026-10-29): the line
 * under the control's title on its page and the dataset's. Statements, never questions.
 */
export const CONTROL_KIND_DESCRIPTIONS: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures:
    'Etiquetado manual de gestos frente a la detección del motor, sin reglas de habitualidad. Mide la precisión del detector.',
  gesture_alerts:
    'Etiquetado manual de alertas frente a las alertas del motor de gestos, con línea base y absolutos. Mide la precisión de las reglas de habitualidad.',
  semantics:
    'Alertas etiquetadas a mano como entrada del motor de veracidad, frente a verdad/mentira. Mide la metodología aislada del detector.',
  coherence:
    'El producto final: motor de gestos y motor de veracidad encadenados, frente a verdad/mentira. Mide el resultado que ve el cliente.',
});

/**
 * What the launch dialog shows for each kind. Only shown — the sentence, the estimate — and never
 * sent: upstream takes the engines and the gesture source from the kind (`controlRunParameters`).
 */
const PRESETS: Readonly<Record<ControlKind, LaunchPreset>> = Object.freeze({
  gestures: {
    engines: ['gestures'],
    gestureSource: 'engine',
    note: 'Solo el motor de gestos: sus detecciones se comparan con las etiquetas.',
  },
  gesture_alerts: {
    engines: ['gestures'],
    gestureSource: 'engine',
    note: 'Solo el motor de gestos: sus alertas se comparan con las alertas etiquetadas.',
  },
  semantics: {
    // Both, as always: coherence requires gestures. The worker reads the labelled alerts instead.
    engines: ['gestures', 'coherence'],
    gestureSource: 'human_labels',
    note: '(Motor Veracidad + etiquetas de alertas gestuales) vs etiquetas de veracidad.',
  },
  coherence: {
    engines: ['gestures', 'coherence'],
    gestureSource: 'engine',
    note: '(Motor Veracidad + Motor Gestos) vs etiquetas de veracidad.',
  },
});

/** What «Ejecutar» shows it launches for a kind: the engines are the kind's, not a choice. */
export function controlLaunchPreset(kind: ControlKind): LaunchPreset {
  return PRESETS[kind];
}

/**
 * What a control run sends from what the launch dialog chose: the configuration, its version and
 * the threshold. The engines and the gesture source stay behind — upstream sets both from the
 * kind, so an execution of a control group always fits it.
 */
export function controlRunParameters(parameters: GroupLaunchParameters): ControlLaunchParameters {
  const sent: ControlLaunchParameters = {
    analysisConfigId: parameters.analysisConfigId,
    thresholdPct: parameters.thresholdPct,
  };

  if (parameters.configVersionId) sent.configVersionId = parameters.configVersionId;

  return sent;
}

// ── Warnings ─────────────────────────────────────────────────────────────────

export interface ControlWarning {
  code: string;
  tone: StatusTone;
  text: string;
}

/** «1 vídeo» / «3 vídeos». */
function videos(count: number): string {
  return count === 1 ? '1 vídeo' : `${count} vídeos`;
}

const NO_EXECUTION: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures: 'Ninguna ejecución con el motor de gestos',
  gesture_alerts: 'Ninguna ejecución con las alertas del motor de gestos',
  semantics: 'Ninguna ejecución con las alertas etiquetadas',
  coherence: 'Ninguna ejecución con gestos y veracidad',
});

/**
 * A count derived from two of upstream's, or null when the two disagree: a negative count would
 * be a number nobody can act on, so the warning is said without one instead.
 */
function difference(total: number, part: number): number | null {
  const value = total - part;

  return value > 0 ? value : null;
}

function wordWarning(code: string, control: ControlGroup): ControlWarning {
  const { checks } = control;

  switch (code) {
    case 'no_execution':
      return { code, tone: 'neutral', text: NO_EXECUTION[control.kind] };
    case 'non_manual_members': {
      const count = difference(checks.members, checks.manualMembers);

      return {
        code,
        tone: 'warning',
        text:
          count === null
            ? 'Hay miembros que no son vídeos'
            : count === 1
              ? '1 miembro no es un vídeo'
              : `${count} miembros no son vídeos`,
      };
    }
    // Gestos's alone since Semántica reads the alerts (2026-10-29). A video marked complete with
    // no labels is a video without gestures and lacks nothing, yet the checks count it among those
    // without labels: they cannot tell it from one never labelled, so it is said without a count —
    // the pending list beside it names the ones that lack them.
    case 'gesture_labels_missing':
      return {
        code,
        tone: 'warning',
        text: 'Hay vídeos sin gestos etiquetados',
      };
    // Said by what is complete: a video may be complete with no labels at all (nothing happens in
    // it), so «incomplete» cannot be counted from the two figures, and «complete» can.
    case 'gesture_labelling_incomplete':
      return {
        code,
        tone: 'warning',
        text: `Etiquetado completo en ${checks.gestureLabellingComplete} de ${videos(checks.manualMembers)}`,
      };
    // Alertas gestuales and Semántica: a video whose alert labelling is not marked complete is
    // left out (Semántica also one complete without alerts: the veracity engine needs them). The
    // checks do not count that document, so no figure is given.
    case 'alert_labels_missing':
      return {
        code,
        tone: 'warning',
        text: 'Hay vídeos sin alertas etiquetadas o con el etiquetado de alertas sin completar',
      };
    // Coherence reads the video's own transcription and skips a video without one.
    case 'transcriptions_not_ready': {
      const count = difference(checks.manualMembers, checks.withTranscription);

      return {
        code,
        tone: 'warning',
        text:
          count === null ? 'Hay vídeos sin transcripción' : `${videos(count)} sin transcripción`,
      };
    }
    // «Verdad o mentira», never «sin etiquetar» alone: on these cards a label is also a gesture's.
    // A video lacking it has neither its lie marked nor «Completo» as truth.
    case 'truth_labels_missing':
      return {
        code,
        tone: 'warning',
        text:
          checks.withoutTruthLabel > 0
            ? `${videos(checks.withoutTruthLabel)} sin verdad o mentira`
            : 'Hay vídeos sin verdad o mentira',
      };
    default:
      // A code added upstream after this build is still worth reading as it was spelled.
      return { code, tone: 'warning', text: `Aviso «${code}»` };
  }
}

/** Upstream's codes, in its order, each worded with the counts that make it actionable. */
export function controlWarnings(control: ControlGroup): ControlWarning[] {
  return control.warnings.map((code) => wordWarning(code, control));
}

// ── Ready, or what is pending ────────────────────────────────────────────────

export interface ControlReadiness {
  tone: StatusTone;
  /** «Listo», «3 vídeos pendientes» or «Sin vídeos». */
  label: string;
  /** What the badge's tooltip says: what a video needs for this kind. */
  hint: string;
}

/** What a video needs before it counts for each kind: upstream's rule, said in words. */
const NEEDS: Readonly<Record<ControlKind, string>> = Object.freeze({
  // Upstream's rule for Gestos: a video without gestures is marked complete with no labels.
  gestures:
    'Cada vídeo necesita su etiquetado de gestos marcado como completo; uno sin gestos se marca completo sin etiquetas.',
  // Its own document (2026-10-29): only the alerts, complete even when empty.
  gesture_alerts:
    'Cada vídeo necesita su etiquetado de alertas marcado como completo; uno sin alertas se marca completo sin etiquetas.',
  // Fed to the veracity engine (2026-10-29): the alerts, complete and at least one. The
  // transcription is not asked for: a video without one is transcribed by its own run.
  semantics:
    'Cada vídeo necesita alertas etiquetadas con el etiquetado de alertas marcado como completo y verdad o mentira.',
  coherence: 'Cada vídeo necesita verdad o mentira.',
});

/**
 * Whether the group meets its kind's rule, upstream's word: at least one video, and none pending.
 * Not ready with nothing pending can only be a group with no video to measure.
 */
export function controlReadiness(control: ControlGroup): ControlReadiness {
  const hint = NEEDS[control.kind];

  if (control.ready) return { tone: 'success', label: 'Listo', hint };

  if (control.pending > 0) {
    return {
      tone: 'warning',
      label: control.pending === 1 ? '1 vídeo pendiente' : `${control.pending} vídeos pendientes`,
      hint,
    };
  }

  return {
    tone: 'neutral',
    label: 'Sin vídeos',
    hint: 'Necesita al menos un vídeo.',
  };
}

const MISSING: Readonly<Record<ControlMissing, string>> = Object.freeze({
  gesture_labels: 'sin gestos etiquetados',
  gesture_labelling_complete: 'etiquetado sin completar',
  // Semántica also leaves out a video marked complete without alerts, so both are said.
  alert_labels: 'sin alertas etiquetadas o con el etiquetado de alertas sin completar',
  // As on the warnings: «sin etiqueta» alone would read as a gesture label.
  truth_label: 'sin verdad o mentira',
  transcription: 'sin transcripción',
});

/** One thing a pending video lacks, in lower case so it reads inside a line. */
export function missingLabel(code: ControlMissing | string): string {
  return (MISSING as Readonly<Record<string, string>>)[code] ?? `falta «${code}»`;
}

/** «Sin gestos etiquetados · sin transcripción»: everything the video lacks, in one line. */
export function pendingVideoLine(video: ControlPendingVideo): string {
  const line = video.missing.map(missingLabel).join(' · ');

  if (line === '') return 'Le falta algo para este control';

  return line.charAt(0).toLocaleUpperCase('es') + line.slice(1);
}

// ── Facts ────────────────────────────────────────────────────────────────────

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * The group, counted, in the long form: the same three facts whatever its kind. No screen draws it
 * now — the scoreboard on «Control» words the same counts short (`ControlGroupCard`). What a kind
 * still lacks is said by its warnings and its readiness.
 */
export function controlFacts(control: ControlGroup): string[] {
  const { checks } = control;

  return [
    videos(checks.members),
    `${checks.withTruthLabel} con verdad o mentira`,
    `${checks.withGestureLabels} con gestos etiquetados`,
  ];
}

// ── Figures ──────────────────────────────────────────────────────────────────

export const GESTURE_CONTROL_TARGETS = Object.freeze({
  detection: 80,
  falsePositives: 20,
  correctVideos: 70,
});

/** Alertas gestuales is held to the same three as Gestos, over the alerts (owner, 2026-10-28). */
export const GESTURE_ALERT_CONTROL_TARGETS = GESTURE_CONTROL_TARGETS;

/** The words of the three figures: Gestos's, and Alertas gestuales's for the same figures over the alerts. */
interface GestureFigureLabels {
  detection: string;
  falsePositives: string;
  correctVideos: string;
}

const GESTURE_FIGURE_LABELS: GestureFigureLabels = Object.freeze({
  detection: 'Detección correcta',
  falsePositives: 'Falsos positivos',
  correctVideos: 'Vídeos correctos',
});

/** Alertas gestuales (product owner, 2026-10-29): the alerts labelled by hand against the engine's. */
const ALERT_FIGURE_LABELS: GestureFigureLabels = Object.freeze({
  detection: 'Alertas detectadas',
  falsePositives: 'Falsas alertas',
  correctVideos: 'Vídeos correctos',
});

export interface ControlFigure {
  key: string;
  label: string;
  value: number | null;
  direction: MetricDirection;
  target: number;
  /** «≥ 80 %», «≤ 20 %». */
  targetText: string;
  status: MetricStatus;
}

function statusOf(direction: MetricDirection, target: number, value: number | null): MetricStatus {
  if (value === null) return 'none';

  return (direction === 'lower' ? value <= target : value >= target) ? 'ok' : 'ko';
}

function figure(
  key: string,
  label: string,
  direction: MetricDirection,
  target: number,
  value: number | null,
): ControlFigure {
  return {
    key,
    label,
    value,
    direction,
    target,
    targetText: `${direction === 'higher' ? '≥' : '≤'} ${formatPct(target)}`,
    status: statusOf(direction, target, value),
  };
}

/**
 * The three figures of a control group, always three: «—» where nothing fits its kind, never
 * «0 %». Detection figures on Gestos; the same three over the alerts on Alertas gestuales, in its
 * words; the product's three on Semántica and Veracidad.
 */
export function controlFigures(control: ControlGroup): ControlFigure[] {
  const metrics = control.metrics;

  if (control.kind === 'gestures') {
    return gestureFigures(metrics?.kind === 'gestures' ? metrics : null);
  }
  if (control.kind === 'gesture_alerts') {
    return gestureFigures(metrics?.kind === 'gestures' ? metrics : null, ALERT_FIGURE_LABELS);
  }

  const block = coherenceBlock(
    metrics?.kind === 'coherence' ? metrics : null,
    metricsReading.method,
  );

  return SERIES_METRICS.map((metric) =>
    figure(
      metric.key,
      metric.label,
      metric.direction,
      metric.target,
      rawValue(block, figureField(metric.key, metricsReading.global)),
    ),
  );
}

/**
 * The section titles of the figure sets, word for word: Gestos's (product owner, 2026-10-16),
 * Veracidad's (2026-10-20) and Alertas gestuales's (2026-10-29), each control one set. Semántica
 * has no sub-heading.
 */
export const DETECTION_METRICS_TITLE = 'Métricas de detección';
export const VERACITY_METRICS_TITLE = 'Métricas de veracidad';
export const ALERT_METRICS_TITLE = 'Métricas de alertas';

/** The sub-heading over a control's figures; null on Semántica, which has none. */
export function controlFiguresTitle(kind: ControlKind): string | null {
  if (kind === 'gestures') return DETECTION_METRICS_TITLE;
  if (kind === 'gesture_alerts') return ALERT_METRICS_TITLE;
  if (kind === 'coherence') return VERACITY_METRICS_TITLE;

  return null;
}

/**
 * The «i» beside each metrics section on «Control» (product owner, 2026-10-21): what it measures,
 * in a line. Semántica's set has no sub-heading of its own, so its «i» sits beside «Métricas».
 */
const FIGURES_INFO: Readonly<Record<ControlKind, string>> = Object.freeze({
  gestures:
    'Cuántos gestos etiquetados a mano detecta el motor de gestos, y cuántas detecciones no tienen etiqueta.',
  gesture_alerts:
    'Cuántas alertas etiquetadas a mano avisa el motor de gestos con sus reglas de habitualidad, y cuántas de sus alertas no tienen etiqueta.',
  semantics:
    'Cuántas mentiras acierta el motor de veracidad en cada vídeo, partiendo de las alertas etiquetadas a mano.',
  coherence:
    'Cuántas mentiras acierta el motor de veracidad en cada vídeo, partiendo de los gestos que detecta el motor de gestos.',
});

export function controlFiguresInfo(kind: ControlKind): string {
  return FIGURES_INFO[kind];
}

/**
 * The configuration the control's latest execution was sealed with, «Gestos base» and «v5», from
 * the control's history (the control answer itself does not carry it); null when that execution is
 * not in the history or recorded none.
 */
export function controlAppliedConfig(
  control: ControlGroup,
  batches: readonly GroupBatch[] | null | undefined,
): { name: string; version: string | null } | null {
  const latest = control.latestExecution?.batch;
  const batch = latest ? batches?.find((item) => item.batch === latest) : undefined;
  if (!batch) return null;

  const parameters = executionParameters(batch);

  return parameters.configName === null
    ? null
    : { name: parameters.configName, version: parameters.configVersion };
}

/**
 * Gestos's three figures over one set of figures, each against its target — in Gestos's words, or
 * in Alertas gestuales's for the same figures counted over the alerts.
 */
function gestureFigures(
  gestures: GestureControlMetrics | null,
  labels: GestureFigureLabels = GESTURE_FIGURE_LABELS,
): ControlFigure[] {
  return [
    figure(
      'detection_pct',
      labels.detection,
      'higher',
      GESTURE_CONTROL_TARGETS.detection,
      gestures?.detectionPct ?? null,
    ),
    figure(
      'false_positive_pct',
      labels.falsePositives,
      'lower',
      GESTURE_CONTROL_TARGETS.falsePositives,
      gestures?.falsePositivePct ?? null,
    ),
    figure(
      'correct_videos_pct',
      labels.correctVideos,
      'higher',
      GESTURE_CONTROL_TARGETS.correctVideos,
      gestures?.correctVideosPct ?? null,
    ),
  ];
}

/**
 * A control's coherence figures under one method: its block when upstream sent every method's, the
 * flat figures only when they are that method's — never another method's under this one's name.
 */
function coherenceBlock(
  metrics: CoherenceControlMetrics | null,
  method: string,
): MethodDelta | undefined {
  if (!metrics) return undefined;
  if (metrics.methods) return metrics.methods[method];

  return metrics.method === method ? metrics : undefined;
}

/**
 * What the gestures figures were counted over, so a percentage is never read without its base:
 * «10 vídeos evaluados · 243 de 340 etiquetas detectadas · 90 de 400 detecciones sin etiqueta».
 * Over the alerts, for Alertas gestuales: «4 vídeos evaluados · 20 de 25 alertas etiquetadas
 * detectadas · 3 de 18 alertas del motor sin etiqueta».
 */
export function gestureMetricsDetail(
  metrics: GestureControlMetrics,
  over: 'gestures' | 'gesture_alerts' = 'gestures',
): string {
  const alerts = over === 'gesture_alerts';

  return [
    `${videos(metrics.videosEvaluated)} ${metrics.videosEvaluated === 1 ? 'evaluado' : 'evaluados'}`,
    `${metrics.labelsDetected} de ${plural(
      metrics.labels,
      alerts ? 'alerta etiquetada detectada' : 'etiqueta detectada',
      alerts ? 'alertas etiquetadas detectadas' : 'etiquetas detectadas',
    )}`,
    `${metrics.falsePositives} de ${plural(
      metrics.detections,
      alerts ? 'alerta del motor sin etiqueta' : 'detección sin etiqueta',
      alerts ? 'alertas del motor sin etiqueta' : 'detecciones sin etiqueta',
    )}`,
  ].join(' · ');
}

/**
 * What the figures were counted over, or the method and reading they are shown in — the user's
 * preferred method and «Métricas globales» when on —; null with no figures.
 */
export function controlFiguresNote(control: ControlGroup): string | null {
  const metrics = control.metrics;

  if (!metrics) return null;
  if (metrics.kind === 'gestures') {
    return gestureMetricsDetail(
      metrics,
      control.kind === 'gesture_alerts' ? 'gesture_alerts' : 'gestures',
    );
  }

  return `Método: ${readingText(metricsReading.method, metricsReading.global)}`;
}

// ── The execution the figures come from ──────────────────────────────────────

const MOMENT = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * «28 sept, 12:30 · Gestos etiquetados · Veracidad 6.1.0 · umbral 80 %»: when the execution the
 * figures come from finished, and what it ran with. Fed by the labels, the gestures engine did not
 * run, so its version is not named: it is not what was measured.
 */
export function controlExecutionLine(execution: ControlExecution | null): string {
  if (!execution) return 'Sin ejecución';

  const parts: string[] = [];
  const fromLabels = execution.gestureSource === 'human_labels';

  if (execution.finishedAt) parts.push(MOMENT.format(execution.finishedAt));

  for (const engine of execution.engines) {
    if (engine === 'gestures' && fromLabels) {
      parts.push('Gestos etiquetados');
      continue;
    }

    const version = execution.engineVersions?.[engine];
    const name = engineLabel(DEFAULT_ENGINE_CATALOGUE, engine);

    parts.push(version ? `${name} ${version}` : name);
  }

  if (execution.thresholdPct !== null) parts.push(`umbral ${formatPct(execution.thresholdPct)}`);

  return parts.join(' · ');
}

// ── Running it ───────────────────────────────────────────────────────────────

/** Semántica is fed the human labels, which only a video has: upstream leaves the rest out. */
function runsVideosOnly(kind: ControlKind): boolean {
  return PRESETS[kind].gestureSource === 'human_labels';
}

/** Members of its group that are not videos, by upstream's checks; never below zero. */
function nonVideoMembers(control: ControlGroup): number {
  return Math.max(0, control.checks.members - control.checks.manualMembers);
}

/**
 * Why a control group cannot be run now, or null when it can: upstream's own refusal, said before
 * the click. A run takes every member but the pending videos — and, for Semántica, the members
 * that are not videos — and reports those in `skipped`; it is refused only with nothing left.
 * `ready` is not a gate: it says the figures speak for the whole group.
 *
 * The count is the group's members, excluded ones included, as upstream's run takes them: an
 * excluded member counted as runnable lets the click through to upstream's refusal, never holds
 * back a run that would go.
 */
export function controlRunRefusal(control: ControlGroup): string | null {
  if (control.ready) return null;
  if (control.members === 0) return 'El grupo no tiene vídeos';

  const leftOut = control.pending + (runsVideosOnly(control.kind) ? nonVideoMembers(control) : 0);

  if (control.members > leftOut) return null;
  if (control.pending === 0) return 'El grupo no tiene vídeos';

  return 'Ningún vídeo tiene aún lo que necesita este control: completa alguno de los pendientes para ejecutar';
}

/**
 * Why «Ejecutar» is off right now, on the card and on the control's page alike, or null: upstream's
 * refusal first, then a launch already on its way — a second one would race it. The control answer
 * carries everything the launch dialog reads (members, busyness), so nothing else is waited for.
 */
export function controlRunBlocker(
  control: ControlGroup,
  state: { launching: boolean },
): string | null {
  return (
    controlRunRefusal(control) ?? (state.launching ? 'Ya se está lanzando una ejecución' : null)
  );
}

/**
 * What a control run will leave out, said in the launch dialog so the toast after it is no
 * surprise: the pending videos and, for Semántica, the members that are not videos. Null when
 * every member runs.
 */
export function controlLaunchOmissions(control: ControlGroup): string | null {
  const parts: string[] = [];

  if (control.pending > 0) {
    parts.push(
      control.pending === 1
        ? '1 vídeo pendiente se omitirá: le falta lo que necesita este control.'
        : `${control.pending} vídeos pendientes se omitirán: les falta lo que necesita este control.`,
    );
  }

  const others = runsVideosOnly(control.kind) ? nonVideoMembers(control) : 0;

  if (others > 0) {
    parts.push(
      others === 1
        ? `1 miembro que no es un vídeo se omitirá: ${CONTROL_KIND_TITLES[control.kind]} solo analiza vídeos.`
        : `${others} miembros que no son vídeos se omitirán: ${CONTROL_KIND_TITLES[control.kind]} solo analiza vídeos.`,
    );
  }

  return parts.length > 0 ? parts.join(' ') : null;
}

// ── One status ───────────────────────────────────────────────────────────────

export interface ControlStatus {
  key: 'running' | 'pending' | 'no_videos' | 'never_run' | 'outdated' | 'ready';
  tone: StatusTone;
  /** «En marcha», «3 vídeos pendientes», «Sin vídeos», «Sin ejecuciones», «Desactualizado», «Listo». */
  label: string;
  /** Why, in a sentence: the badge's tooltip. */
  hint: string;
  /** Why it is outdated, one sentence per change. Empty for any other status. */
  reasons: string[];
  /** What the answer could not check, or null. */
  note: string | null;
}

const READY_HINT =
  'Su última ejecución usó las versiones actuales de su configuración y de sus motores, y sus vídeos no han cambiado desde entonces';

/**
 * One status for a control group, never two side by side (product owner, 2026-10-06): «Listo» and
 * «Desactualizado» together read as a contradiction. Upstream keeps its flags apart — `ready` for
 * the videos, `reanalysis` for its latest execution — and the first that applies is the one said:
 *
 * 1. «En marcha»: something of it moves or waits, and whatever it says now is about to change.
 * 2. «Sin ejecuciones»: nothing that fits its kind ever ran on it; «Sin vídeos» when it has none.
 * 3. «Desactualizado», with why: its configuration, an engine or its videos moved after its latest
 *    execution, so its figures no longer speak for it. Before the pending videos since
 *    2026-10-28 (product owner): a control with a few videos still to label hid that its figures
 *    were stale — on Veracidad, where some video always lacks its label or transcription, the
 *    chip never showed.
 * 4. «N vídeos pendientes»: the group does not yet meet its kind's rule.
 * 5. «Listo»: none of the above.
 *
 * The control answer carries its own `reanalysis`, runs and waiting executions (2026-10-28), so
 * nothing is waited for. Busy is read from them and the queue (`activity`) on the board, and from
 * the control's own executions on its page, which sees them first.
 */
export function controlStatus(
  control: ControlGroup,
  activity: { running: number; waiting: number } | null,
  configs?: readonly Pick<AnalysisConfigSummary, 'id' | 'name'>[] | null,
  executions?: Pick<GroupExecutionList, 'items' | 'waiting'> | null,
): ControlStatus {
  const plain = { reasons: [], note: null };
  const flag = reanalysisFlag(control.reanalysis, configNameResolver(control, configs));

  if (launchWillQueue(control, { activity, executions }) || flag?.kind === 'running') {
    return {
      key: 'running',
      tone: 'info',
      label: 'En marcha',
      hint: 'Tiene una ejecución en marcha o en cola: su estado se sabrá al terminar',
      ...plain,
    };
  }

  const readiness = controlReadiness(control);

  if (!control.ready && control.pending === 0) {
    return { key: 'no_videos', ...readiness, ...plain };
  }

  if (!control.latestExecution) {
    return {
      key: 'never_run',
      tone: 'neutral',
      label: 'Sin ejecuciones',
      hint: NO_EXECUTION[control.kind],
      ...plain,
    };
  }

  if (flag?.kind === 'outdated') {
    return {
      key: 'outdated',
      tone: flag.tone,
      label: flag.label,
      hint: flag.reasons.join('; '),
      reasons: flag.reasons,
      note: flag.note,
    };
  }

  if (!control.ready) {
    return { key: 'pending', ...readiness, ...plain };
  }

  return {
    key: 'ready',
    tone: 'success',
    label: 'Listo',
    hint: READY_HINT,
    ...plain,
  };
}

/** Its warnings but the one the status already says: «Sin ejecuciones» is the status itself. */
export function controlStatusWarnings(control: ControlGroup): ControlWarning[] {
  return controlWarnings(control).filter((warning) => warning.code !== 'no_execution');
}
